<template>
  <div ref="root" class="dashboard-trend" :class="{ 'compliance-trend': metric === 'complianceRate', 'active-listings-trend': metric === 'activeListings', 'expanded-trend': expanded }">
    <label class="trend-period-control" :title="periodDescription">
      <span class="trend-period-summary" aria-hidden="true">
        <span v-if="showChange" class="trend-change" :aria-label="periodDescription"><span v-if="changeDirection" class="trend-direction">{{ changeDirection }}</span><strong>{{ changeAmount }}</strong></span>
        <span v-else class="trend-change-unavailable">—</span>
        <span class="trend-period-duration">· {{ displayedDays }} {{ displayedDays === 1 ? 'day' : 'days' }}</span>
        <i class="bi-chevron-down trend-period-caret" />
      </span>
      <select v-model.number="days" class="trend-period" :aria-label="`${label} history period. ${periodDescription}`">
        <option :value="30">Last 30 days</option><option :value="7">Last 7 days</option>
        <option v-if="expanded" :value="90">Last 90 days</option>
      </select>
    </label>
    <p v-if="loading" class="trend-status">Loading history…</p>
    <p v-else-if="error" class="trend-status">History unavailable. <button type="button" @click="$emit('retry')">Retry</button></p>
    <template v-else>
      <button v-if="hasHistory" type="button" class="trend-chart" :aria-label="`${label} chart scale: ${formatValue(scaleDomain.min)} to ${formatValue(scaleDomain.max)}. Use left and right arrow keys to explore saved values.${gapPaths.length ? ' Gray dashed lines bridge gaps; only recorded values can be inspected.' : ''}`" :aria-pressed="expanded ? undefined : scaleVisible"
        :aria-describedby="activePoint ? tooltipId : undefined"
        @pointerenter="enterChart" @pointermove="inspectPoint" @pointerdown="inspectPoint" @pointerleave="leaveChart" @pointercancel="leaveChart"
        @focus="focusChart" @blur="blurChart" @click="toggleScale" @keydown.esc.prevent="dismissScale"
        @keydown.left.prevent="movePoint(-1)" @keydown.right.prevent="movePoint(1)" @keydown.home.prevent="selectPoint(0)" @keydown.end.prevent="selectPoint(plotted.length - 1)">
        <svg class="trend-sparkline" :viewBox="`0 0 ${width} ${chartHeight}`" :style="{ height: `${chartHeight}px` }" role="img" :aria-label="sparklineLabel">
          <g class="trend-context" aria-hidden="true">
            <line :x1="leftInset" :x2="width - 4" :y1="chartBottom" :y2="chartBottom" />
            <line :x1="leftInset" :x2="leftInset" :y1="chartBottom" :y2="chartBottom + 3" />
            <line :x1="width - 4" :x2="width - 4" :y1="chartBottom" :y2="chartBottom + 3" />
            <text v-if="width - leftInset >= 95" :x="leftInset" :y="chartHeight - 4" text-anchor="start">{{ shortDate(trend.startDate) }}</text>
            <text :x="width - 4" :y="chartHeight - 4" text-anchor="end">{{ shortDate(trend.endDate) }}</text>
          </g>
          <g v-if="scaleVisible" class="trend-scale" aria-hidden="true">
            <g v-for="tick in scaleTicks" :key="tick.value">
              <line v-if="tick.y !== chartBottom" :x1="leftInset" :x2="width - 4" :y1="tick.y" :y2="tick.y" />
              <text :x="leftInset - 5" :y="tick.y" text-anchor="end" dominant-baseline="middle">{{ tick.label }}</text>
            </g>
            <line :x1="leftInset" :x2="leftInset" y1="8" :y2="chartBottom" />
            <line :x1="width - 4" :x2="width - 4" y1="8" :y2="chartBottom" />
          </g>
          <path v-for="(path, index) in gapPaths" :key="`gap-${index}`" class="trend-gap" :d="path" />
          <path v-for="(path, index) in paths" :key="`series-${index}`" class="trend-series" :d="path" />
          <circle v-for="point in plotted" :key="point.date" :cx="point.x" :cy="point.y" r="0.75" />
          <g v-if="activePoint" class="trend-focus" aria-hidden="true">
            <line :x1="activePoint.x" :x2="activePoint.x" y1="8" :y2="chartBottom" />
            <circle :cx="activePoint.x" :cy="activePoint.y" r="2" />
          </g>
        </svg>
        <span v-if="activePoint" :id="tooltipId" role="tooltip" class="trend-tooltip" :class="{ 'below-chart': tooltipBelow }" :style="tooltipStyle">
          <span class="trend-tooltip-date">{{ fullDate(activePoint.date) }}</span>
          <strong>{{ formatValue(activePoint.value) }}</strong>
        </span>
      </button>
      <p v-else class="trend-status">Not enough comparable history</p>
      <p v-if="expanded && trend.endDate" class="trend-status">{{ changeLabel ? `Compared with ${shortDate(trend.comparisonDate)}.` : 'No earlier comparable snapshot in this period.' }} Through {{ fullDate(trend.endDate) }}.</p>
    </template>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { buildDashboardTrend, historyComplianceRate, isRateMetric } from './dashboardTrendData';
import { fmtInt, fmtPct } from '../../../../dashboard/public/js/format.js';

const props = defineProps({
  metric: { type: String, required: true }, value: { type: Number, default: null }, generatedAt: String, snapshotVersion: Number,
  rows: { type: Array, default: () => [] }, complianceOptions: Object, loading: Boolean, error: String,
  expanded: Boolean, metricLabel: String
});
defineEmits(['retry']);
const days = ref(7);
const root = ref(null);
const width = ref(props.expanded ? 480 : 180);
const chartHeight = computed(() => props.expanded ? 240 : 112);
const chartBottom = computed(() => chartHeight.value - 22);
const hovered = ref(false);
const focused = ref(false);
const scalePinned = ref(false);
const scaleDismissed = ref(false);
const selectedDate = ref(null);
const tooltipBelow = ref(false);
const tooltipId = `dashboard-trend-tooltip-${getCurrentInstance().uid}`;
const scaleVisible = computed(() => props.expanded || !scaleDismissed.value && (hovered.value || focused.value || scalePinned.value));
function toggleScale() {
  scalePinned.value = !scalePinned.value;
  scaleDismissed.value = !scalePinned.value;
  if (scalePinned.value && !selectedDate.value) selectPoint(plotted.value.length - 1);
}
const isRate = computed(() => isRateMetric(props.metric));
const label = computed(() => props.metricLabel || (props.metric === 'complianceRate' ? 'Compliance rate' : props.metric === 'verificationRate' ? 'Verification rate' : 'Active listings'));
const rows = computed(() => props.metric === 'complianceRate' ? props.rows.map(row => ({ ...row, complianceRate: historyComplianceRate(row, props.complianceOptions) })) : props.rows);
const trend = computed(() => buildDashboardTrend({ rows: rows.value, metric: props.metric, value: props.value, generatedAt: props.generatedAt, snapshotVersion: props.snapshotVersion, days: days.value }));
const validPoints = computed(() => trend.value.points.filter(point => point.value != null));
const hasHistory = computed(() => validPoints.value.length >= 1);
const formatValue = value => isRate.value ? fmtPct(value) : `${fmtInt(value)} ${value === 1 ? 'listing' : 'listings'}`;
const shortDate = date => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`));
const fullDate = date => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`));
const compactCount = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const formatScale = value => isRate.value ? fmtPct(value) : value >= 10000 ? compactCount.format(value) : fmtInt(value);
const changeAmount = computed(() => {
  if (trend.value.change == null) return null;
  const magnitude = Math.abs(trend.value.change);
  if (magnitude === 0) return 'no change';
  const roundedPoints = (magnitude * 100).toFixed(1);
  const amount = isRate.value ? Number(roundedPoints) === 0 ? '<0.1' : roundedPoints : fmtInt(magnitude);
  return `${amount}${isRate.value ? ' pp' : ''}`;
});
const changeLabel = computed(() => {
  const change = trend.value.change;
  if (change == null) return null;
  if (change === 0) return 'No change';
  return `${change > 0 ? '+' : '−'}${changeAmount.value}${isRate.value ? '' : Math.abs(change) === 1 ? ' listing' : ' listings'}`;
});
const showChange = computed(() => !props.loading && !props.error && trend.value.change != null);
const displayedDays = computed(() => showChange.value ? trend.value.comparisonDays : days.value);
const changeDirection = computed(() => trend.value.change > 0 ? '↑' : trend.value.change < 0 ? '↓' : null);
const periodDescription = computed(() => props.loading ? 'Loading history.' : props.error ? 'History unavailable.' : showChange.value
  ? `${changeLabel.value.replace('pp', 'percentage points')}, compared with ${trend.value.comparisonDays} ${trend.value.comparisonDays === 1 ? 'day' : 'days'} before ${trend.value.endDate}${trend.value.comparisonDays < days.value ? `. Selected window: ${days.value} days; using available history from ${trend.value.comparisonDate}.` : ''}`
  : 'Change unavailable: no earlier comparable snapshot.');
const sparklineLabel = computed(() => `${label.value}, ${trend.value.startDate} to ${trend.value.endDate}. ${validPoints.value.length} comparable saved days. ${changeLabel.value ? `${changeLabel.value.replace('pp', 'percentage points')} versus ${trend.value.comparisonDays} ${trend.value.comparisonDays === 1 ? 'day' : 'days'} earlier.` : 'No earlier comparable snapshot.'} Scale: ${formatValue(scaleDomain.value.min)} to ${formatValue(scaleDomain.value.max)}. Gaps are missing or incompatible snapshots. Gray dashed connectors are visual guides, not measured values.${props.metric === 'complianceRate' ? ' Uses the selected calculation options.' : props.metric === 'verificationRate' ? ' Deferred listings are excluded.' : ' Change is the net active listing count.'}`);
const scaleDomain = computed(() => {
  if (!validPoints.value.length) return { min: 0, max: isRate.value ? 1 : 10 };
  const values = validPoints.value.map(point => point.value);
  const low = Math.min(...values), high = Math.max(...values);
  // A minimum visible range keeps tiny changes from looking like large swings.
  const span = Math.max(high - low, isRate.value ? .08 : Math.max(10, high * .1));
  const power = 10 ** Math.floor(Math.log10(span / 2));
  const step = [1, 2, 5, 10].find(value => value * power >= span / 2) * power;
  let min = Math.max(0, Math.floor(low / step) * step);
  let max = Math.max(min + 2 * step, Math.ceil(high / step) * step);
  if (isRate.value && max > 1) { max = 1; min = Math.max(0, Math.min(min, max - 2 * step)); }
  return { min, max };
});
const scaleTicks = computed(() => {
  const { min, max } = scaleDomain.value;
  return [max, (min + max) / 2, min].map((value, index) => ({ value, y: 8 + index * (chartBottom.value - 8) / 2, label: formatScale(value) }));
});
// Use the full plotting width at rest; make room for labels only while shown.
const leftInset = computed(() => scaleVisible.value
  ? Math.min(width.value * .45, Math.max(32, ...scaleTicks.value.map(tick => tick.label.length * 6.5 + 5))) : 4);
const coordinates = computed(() => {
  if (!validPoints.value.length) return [];
  const { min, max } = scaleDomain.value;
  return trend.value.points.map((point, index) => ({ ...point,
    x: leftInset.value + index / days.value * (width.value - leftInset.value - 4),
    y: point.value == null ? null : chartBottom.value - (point.value - min) / (max - min) * (chartBottom.value - 8)
  }));
});
const plotted = computed(() => coordinates.value.filter(point => point.y != null));
const activePoint = computed(() => hasHistory.value && scaleVisible.value && !props.loading && !props.error
  ? plotted.value.find(point => point.date === selectedDate.value) : null);
const tooltipStyle = computed(() => {
  const tooltipWidth = Math.min(156, Math.max(0, width.value - 8));
  const left = Math.max(4, Math.min(width.value - tooltipWidth - 4, (activePoint.value?.x ?? 0) - tooltipWidth / 2));
  return { width: `${tooltipWidth}px`, left: `${left}px` };
});
function inspectPoint(event) {
  const rect = event.currentTarget.getBoundingClientRect();
  if (!rect.width || !Number.isFinite(event.clientX) || !plotted.value.length) return;
  // Use the rendered width so the hit area remains accurate when resized or zoomed.
  const x = (event.clientX - rect.left) * width.value / rect.width;
  const nearest = plotted.value.reduce((best, point) => Math.abs(point.x - x) < Math.abs(best.x - x) ? point : best);
  selectedDate.value = nearest.date;
  tooltipBelow.value = rect.top < 64;
}
function enterChart(event) {
  hovered.value = event.pointerType !== 'touch';
  scaleDismissed.value = false;
  inspectPoint(event);
}
function leaveChart() {
  hovered.value = false;
  if (!focused.value && !scalePinned.value) selectedDate.value = null;
}
function focusChart(event) {
  focused.value = true;
  scaleDismissed.value = false;
  tooltipBelow.value = event.currentTarget.getBoundingClientRect().top < 64;
  if (!selectedDate.value) selectPoint(plotted.value.length - 1);
}
function blurChart() {
  focused.value = false;
  scalePinned.value = false;
  selectedDate.value = null;
}
function dismissScale() {
  scaleDismissed.value = true;
  scalePinned.value = false;
  selectedDate.value = null;
}
function selectPoint(index) {
  if (!plotted.value.length) return;
  selectedDate.value = plotted.value[Math.max(0, Math.min(plotted.value.length - 1, index))].date;
  scaleDismissed.value = false;
}
function movePoint(direction) {
  const index = plotted.value.findIndex(point => point.date === selectedDate.value);
  selectPoint((index < 0 ? plotted.value.length - 1 : index) + direction);
}
watch([days, () => props.loading, () => props.error, () => props.metric, () => props.metricLabel], () => {
  selectedDate.value = null;
  scalePinned.value = false;
  if (props.loading || props.error) { hovered.value = false; focused.value = false; }
});
const paths = computed(() => {
  const result = [];
  let run = [];
  const finish = () => { if (run.length > 1) result.push(run.map((point, index) => `${index ? 'L' : 'M'}${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(' ')); run = []; };
  for (const point of coordinates.value) { if (point.y == null) finish(); else run.push(point); }
  finish();
  return result;
});
const gapPaths = computed(() => {
  const result = [];
  let previous = null;
  let gap = false;
  for (const point of coordinates.value) {
    if (point.y == null) { gap = true; continue; }
    // Leading gaps get a flat guide at the first saved value. These paths do
    // not add observations to the tooltips or change calculation, and trailing
    // gaps stay empty because there is no next saved value to connect to.
    if (gap) {
      const from = previous || { x: leftInset.value, y: point.y };
      result.push(`M${from.x.toFixed(2)},${from.y.toFixed(2)} L${point.x.toFixed(2)},${point.y.toFixed(2)}`);
    }
    previous = point;
    gap = false;
  }
  return result;
});
let observer;
onMounted(() => {
  if (typeof ResizeObserver === 'undefined') return;
  observer = new ResizeObserver(entries => { if (entries[0].contentRect.width > 0) width.value = entries[0].contentRect.width; });
  observer.observe(root.value);
});
onBeforeUnmount(() => observer?.disconnect());
</script>

<style scoped>
.dashboard-trend { flex: 1 1 130px; min-width: 0; max-width: 220px; margin-left: auto; text-align: right; }
.expanded-trend { max-width: none; width: 100%; margin: 0; }
.expanded-trend .trend-period-control { border: 1px solid var(--border); padding: 6px 10px; font-size: 12px; margin-bottom: 20px; }
.expanded-trend .trend-status { text-align: left; }
.trend-period-control { position: relative; display: inline-flex; align-items: center; max-width: 100%; min-height: 26px; padding: 3px 0; border-radius: 4px; color: var(--text-secondary); font-size: 11px; line-height: 1.5; }
.trend-period-summary { display: inline-flex; align-items: center; gap: 6px; pointer-events: none; white-space: nowrap; font-variant-numeric: tabular-nums; }
.trend-period-caret { font-size: 9px; }
/* Keep the native picker for keyboard and touch, with the compact summary as its face. */
.trend-period { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; font: inherit; }
.trend-period-control:focus-within, button:focus-visible { outline: 2px solid var(--blue); outline-offset: 2px; }
.trend-period-control:hover { color: var(--text-primary); }
.trend-chart { position: relative; display: block; width: 100%; text-decoration: none; border-radius: 3px; }
.trend-sparkline { display: block; width: 100%; height: 112px; overflow: visible; }
.trend-context { pointer-events: none; }
.trend-context line { stroke: var(--border-hover); stroke-width: 1; opacity: .6; }
.trend-context text { fill: var(--text-secondary); opacity: .8; font: 10px var(--bs-font-sans-serif, sans-serif); }
.trend-scale { pointer-events: none; }
.trend-scale line { stroke: var(--border-hover); stroke-width: 1; opacity: .7; }
.trend-scale text { fill: var(--text-secondary); font: 11px var(--bs-font-sans-serif, sans-serif); }
.trend-sparkline path { fill: none; stroke: var(--blue); stroke-width: 2; stroke-linejoin: round; stroke-linecap: round; }
.trend-sparkline circle { fill: var(--blue); }
.compliance-trend path { stroke: var(--green); }
.compliance-trend circle { fill: var(--green); }
.active-listings-trend path { stroke: #3f4247; }
.active-listings-trend circle { fill: #3f4247; }
.trend-sparkline .trend-gap { stroke: var(--text-tertiary, #9e9e9e); stroke-width: 1; stroke-dasharray: 2 4; opacity: .6; pointer-events: none; }
.trend-focus { pointer-events: none; }
.trend-focus line { stroke: var(--text-secondary); stroke-width: 1; stroke-dasharray: 2 3; opacity: .5; }
.trend-focus circle { fill: var(--surface); stroke: var(--blue); stroke-width: 1.5; }
.compliance-trend .trend-focus circle { stroke: var(--green); }
.active-listings-trend .trend-focus circle { stroke: #3f4247; }
.trend-tooltip { position: absolute; bottom: calc(100% + 8px); z-index: 10; box-sizing: border-box; display: grid; gap: 3px; padding: 8px 10px; border: 1px solid var(--border); border-radius: 6px; background: var(--surface); color: var(--text-primary); box-shadow: 0 4px 16px #0002; font-size: 12px; line-height: 1.4; text-align: left; pointer-events: none; }
.trend-tooltip.below-chart { top: calc(100% + 8px); bottom: auto; }
.trend-tooltip-date { color: var(--text-secondary); font-size: 11px; }
.trend-tooltip strong { font-weight: 600; font-variant-numeric: tabular-nums; }
.trend-change { display: inline-flex; align-items: center; gap: 3px; color: var(--text-primary); }
.trend-change strong { font-weight: 500; }
.trend-status { margin: 6px 0 0; color: var(--text-secondary); font-size: 11px; line-height: 1.5; }
button { padding: 0; border: 0; background: transparent; color: inherit; text-decoration: underline; }
@media (pointer: coarse) { .trend-period-control { min-height: 44px; } .trend-period { font-size: 16px; } button { min-height: 44px; } }
</style>
